import React, { useState, useRef, useEffect } from 'react';
import { Send, Zap, Mic, ArrowLeft, AlertTriangle, TrendingUp, TrendingDown, Heart, Star, MessageSquare, ThumbsUp, ThumbsDown, Minus, Globe, Twitter, Linkedin, Hash, Calendar, Award } from 'lucide-react';
import { AGENT_THUMBS } from './V4BriefingShared';
import { SENTIMENT_DATA, SENTIMENT_CHAT_RESPONSES } from '../../data/sentimentData';

const AreaChart = ({ data, alert, trendUp, isDark, uid }) => {
  if (!data || data.length < 2) return null;
  const padX = 0;
  const padY = 4;
  const vw = 200;
  const vh = 48;
  const min = Math.min(...data) - (Math.max(...data) - Math.min(...data)) * 0.15;
  const max = Math.max(...data) + (Math.max(...data) - Math.min(...data)) * 0.15;
  const range = max - min || 1;
  const points = data.map((v, i) => ({
    x: padX + (i / (data.length - 1)) * (vw - padX * 2),
    y: padY + (vh - padY * 2) - ((v - min) / range) * (vh - padY * 2),
  }));
  const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
  const areaPath = `${linePath} L${points[points.length - 1].x.toFixed(1)},${vh} L${points[0].x.toFixed(1)},${vh} Z`;
  const strokeColor = alert ? '#f87171' : trendUp ? '#34d399' : '#818cf8';
  const gradientId = `grad-${uid}`;
  const last = points[points.length - 1];

  return (
    <svg viewBox={`0 0 ${vw} ${vh}`} preserveAspectRatio="none" className="w-full" style={{ height: '48px' }}>
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={strokeColor} stopOpacity={isDark ? 0.2 : 0.15} />
          <stop offset="100%" stopColor={strokeColor} stopOpacity={0} />
        </linearGradient>
      </defs>
      <path d={areaPath} fill={`url(#${gradientId})`} />
      <path d={linePath} fill="none" stroke={strokeColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
      {points.map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y} r={i === points.length - 1 ? 2.5 : 1} fill={strokeColor} opacity={i === points.length - 1 ? 1 : 0.4} />
      ))}
    </svg>
  );
};

const TrendCard = ({ item, isDark, idx }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const cardBg = item.alert ? (isDark ? 'bg-red-950/15' : 'bg-red-50/40') : (isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]');
  const cardBorder = item.alert ? (isDark ? 'border-red-500/15' : 'border-red-200/50') : border;

  return (
    <div className={`${cardBg} border ${cardBorder} rounded-xl overflow-hidden`} data-testid={`sentiment-trend-${item.label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="px-3.5 pt-3 pb-1">
        <p className={`text-[10px] ${ts}`}>{item.label}</p>
        <div className="flex items-end justify-between mt-0.5">
          <p className={`text-lg font-bold ${item.alert ? (isDark ? 'text-red-400' : 'text-red-600') : tp}`}>{item.value}</p>
          <div className="flex items-center gap-1 pb-0.5">
            {item.trendUp ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            <span className={`text-[10px] font-medium ${item.trendUp ? (isDark ? 'text-emerald-400' : 'text-emerald-600') : (isDark ? 'text-red-400' : 'text-red-500')}`}>{item.trend}</span>
          </div>
        </div>
      </div>
      <AreaChart data={item.data} alert={item.alert} trendUp={item.trendUp} isDark={isDark} uid={`trend-${idx}`} />
    </div>
  );
};

const MetricPill = ({ item, isDark }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  return (
    <div className={`${isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]'} border ${border} rounded-xl px-3 py-2.5 text-center`}>
      <p className={`text-[9px] ${ts}`}>{item.label}</p>
      <p className={`text-base font-bold mt-0.5 ${tp}`}>{item.value}</p>
      <span className={`text-[9px] font-medium ${item.trendUp ? (isDark ? 'text-emerald-400' : 'text-emerald-600') : (isDark ? 'text-red-400' : 'text-red-500')}`}>{item.trend}</span>
    </div>
  );
};

const FeedbackCard = ({ fb, isDark }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const sentimentIcon = {
    positive: <ThumbsUp className={`w-3 h-3 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} />,
    negative: <ThumbsDown className={`w-3 h-3 ${isDark ? 'text-red-400' : 'text-red-500'}`} />,
    neutral: <Minus className={`w-3 h-3 ${isDark ? 'text-amber-400' : 'text-amber-500'}`} />,
  };
  const sentimentBg = {
    positive: isDark ? 'bg-emerald-500/10' : 'bg-emerald-50',
    negative: isDark ? 'bg-red-500/10' : 'bg-red-50/60',
    neutral: isDark ? 'bg-amber-500/10' : 'bg-amber-50',
  };

  return (
    <div className={`${sentimentBg[fb.sentiment]} border ${border} rounded-lg px-3 py-2.5`} data-testid={`feedback-${fb.id}`}>
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          {sentimentIcon[fb.sentiment]}
          <span className={`text-[11px] font-semibold ${tp}`}>{fb.customer}</span>
        </div>
        <div className="flex items-center gap-0.5">
          {Array.from({ length: 5 }).map((_, i) => (
            <Star key={i} className={`w-2.5 h-2.5 ${i < fb.rating ? (isDark ? 'text-amber-400 fill-amber-400' : 'text-amber-500 fill-amber-500') : (isDark ? 'text-white/10' : 'text-slate-200')}`} />
          ))}
        </div>
      </div>
      <p className={`text-[10px] leading-relaxed ${ts} italic`}>"{fb.excerpt}"</p>
      <div className="flex items-center gap-2 mt-1.5">
        <span className={`text-[8px] px-1.5 py-0.5 rounded-full ${isDark ? 'bg-white/[0.04] text-white/40' : 'bg-black/[0.03] text-slate-500'}`}>{fb.source}</span>
        <span className={`text-[8px] ${isDark ? 'text-white/30' : 'text-slate-500'}`}>{fb.date}</span>
      </div>
    </div>
  );
};

const SentimentBar = ({ positive, negative, neutral, isDark }) => {
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  return (
    <div>
      <div className="flex h-3 rounded-full overflow-hidden" style={{ backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)' }}>
        <div className="h-full bg-emerald-500/50 rounded-l-full" style={{ width: `${positive}%` }} />
        <div className="h-full bg-amber-500/30" style={{ width: `${neutral}%` }} />
        <div className="h-full bg-red-500/40 rounded-r-full" style={{ width: `${negative}%` }} />
      </div>
      <div className="flex items-center gap-4 mt-1">
        <span className={`text-[8px] flex items-center gap-1 ${ts}`}><span className="w-2 h-2 rounded-sm bg-emerald-500/50" /> Positive {positive}%</span>
        <span className={`text-[8px] flex items-center gap-1 ${ts}`}><span className="w-2 h-2 rounded-sm bg-amber-500/30" /> Neutral {neutral}%</span>
        <span className={`text-[8px] flex items-center gap-1 ${ts}`}><span className="w-2 h-2 rounded-sm bg-red-500/40" /> Negative {negative}%</span>
      </div>
    </div>
  );
};

const BrandReputationSection = ({ data, isDark }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const cardBg = isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]';

  const platformIcons = { 'Twitter/X': Twitter, 'LinkedIn': Linkedin, 'Reddit': Hash, 'Product Hunt': Award };

  return (
    <div className="space-y-4" data-testid="brand-reputation-section">
      {/* Review platforms row */}
      <div className="grid grid-cols-3 gap-3">
        {/* Google */}
        <div className={`${cardBg} border ${border} rounded-xl p-3`} data-testid="brand-google">
          <div className="flex items-center gap-2 mb-2">
            <Globe className={`w-4 h-4 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
            <span className={`text-[11px] font-semibold ${tp}`}>Google Reviews</span>
          </div>
          <div className="flex items-center gap-2 mb-2">
            <span className={`text-xl font-bold ${tp}`}>{data.google.rating}</span>
            <div className="flex items-center gap-0.5">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} className={`w-3 h-3 ${i < Math.round(data.google.rating) ? (isDark ? 'text-amber-400 fill-amber-400' : 'text-amber-500 fill-amber-500') : (isDark ? 'text-white/10' : 'text-slate-200')}`} />
              ))}
            </div>
            <span className={`text-[9px] ${ts}`}>{data.google.reviews} reviews</span>
            <span className={`text-[9px] font-medium ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>{data.google.trend}</span>
          </div>
          <div className="space-y-1.5">
            {data.google.recent.map((r, i) => (
              <div key={i} className={`text-[9px] ${ts} flex items-start gap-1.5`}>
                <div className="flex items-center gap-0.5 flex-shrink-0 mt-0.5">
                  {Array.from({ length: r.rating }).map((_, j) => (
                    <Star key={j} className={`w-2 h-2 ${isDark ? 'text-amber-400 fill-amber-400' : 'text-amber-500 fill-amber-500'}`} />
                  ))}
                </div>
                <span className="truncate">"{r.text}"</span>
              </div>
            ))}
          </div>
        </div>

        {/* G2 */}
        <div className={`${cardBg} border ${border} rounded-xl p-3`} data-testid="brand-g2">
          <div className="flex items-center gap-2 mb-2">
            <Award className={`w-4 h-4 ${isDark ? 'text-orange-400' : 'text-orange-600'}`} />
            <span className={`text-[11px] font-semibold ${tp}`}>G2</span>
          </div>
          <div className="flex items-center gap-2 mb-2">
            <span className={`text-xl font-bold ${tp}`}>{data.g2.rating}</span>
            <div className="flex items-center gap-0.5">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} className={`w-3 h-3 ${i < Math.round(data.g2.rating) ? (isDark ? 'text-amber-400 fill-amber-400' : 'text-amber-500 fill-amber-500') : (isDark ? 'text-white/10' : 'text-slate-200')}`} />
              ))}
            </div>
            <span className={`text-[9px] ${ts}`}>{data.g2.reviews} reviews</span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {data.g2.badges.map(b => (
              <span key={b} className={`text-[8px] font-medium px-2 py-0.5 rounded-full ${isDark ? 'bg-orange-500/10 text-orange-400 border border-orange-500/20' : 'bg-orange-50 text-orange-600 border border-orange-200'}`}>{b}</span>
            ))}
          </div>
        </div>

        {/* Capterra */}
        <div className={`${cardBg} border ${border} rounded-xl p-3`} data-testid="brand-capterra">
          <div className="flex items-center gap-2 mb-2">
            <Star className={`w-4 h-4 ${isDark ? 'text-teal-400' : 'text-teal-600'}`} />
            <span className={`text-[11px] font-semibold ${tp}`}>Capterra</span>
          </div>
          <div className="flex items-center gap-2 mb-2">
            <span className={`text-xl font-bold ${tp}`}>{data.capterra.rating}</span>
            <div className="flex items-center gap-0.5">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} className={`w-3 h-3 ${i < Math.round(data.capterra.rating) ? (isDark ? 'text-amber-400 fill-amber-400' : 'text-amber-500 fill-amber-500') : (isDark ? 'text-white/10' : 'text-slate-200')}`} />
              ))}
            </div>
            <span className={`text-[9px] ${ts}`}>{data.capterra.reviews} reviews</span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {data.capterra.badges.map(b => (
              <span key={b} className={`text-[8px] font-medium px-2 py-0.5 rounded-full ${isDark ? 'bg-teal-500/10 text-teal-400 border border-teal-500/20' : 'bg-teal-50 text-teal-600 border border-teal-200'}`}>{b}</span>
            ))}
          </div>
        </div>
      </div>

      {/* Social Listening */}
      <div className={`${cardBg} border ${border} rounded-xl p-4`} data-testid="social-listening">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Globe className={`w-4 h-4 ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`} />
            <span className={`text-[11px] font-semibold ${tp}`}>Social Listening</span>
          </div>
          <div className="flex items-center gap-3">
            <span className={`text-[10px] ${tp}`}><span className="font-bold">{data.socialListening.mentions}</span> mentions</span>
            <span className={`text-[9px] font-medium ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>{data.socialListening.mentionsTrend}</span>
          </div>
        </div>

        <SentimentBar positive={data.socialListening.positive} negative={data.socialListening.negative} neutral={data.socialListening.neutral} isDark={isDark} />

        <div className="grid grid-cols-2 gap-4 mt-3">
          {/* Platform breakdown */}
          <div>
            <p className={`text-[9px] font-semibold mb-2 ${ts}`}>Platform Breakdown</p>
            <div className="space-y-1.5">
              {data.socialListening.platforms.map(p => {
                const Icon = platformIcons[p.name] || Globe;
                return (
                  <div key={p.name} className="flex items-center gap-2">
                    <Icon className={`w-3 h-3 ${ts}`} />
                    <span className={`text-[9px] w-20 ${tp}`}>{p.name}</span>
                    <div className="flex-1 h-1.5 rounded-full" style={{ backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)' }}>
                      <div className="h-full rounded-full bg-indigo-500/40 transition-all duration-500" style={{ width: `${(p.mentions / data.socialListening.mentions) * 100}%` }} />
                    </div>
                    <span className={`text-[9px] font-medium w-8 text-right ${tp}`}>{p.mentions}</span>
                    <span className={`text-[8px] w-10 text-right ${p.sentiment >= 70 ? (isDark ? 'text-emerald-400' : 'text-emerald-600') : p.sentiment >= 60 ? (isDark ? 'text-amber-400' : 'text-amber-500') : (isDark ? 'text-red-400' : 'text-red-500')}`}>{p.sentiment}% pos</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Top topics */}
          <div>
            <p className={`text-[9px] font-semibold mb-2 ${ts}`}>Top Topics</p>
            <div className="space-y-1.5">
              {data.socialListening.topTopics.map((t, i) => {
                const sentColor = t.sentiment === 'positive' ? (isDark ? 'text-emerald-400' : 'text-emerald-600') : t.sentiment === 'negative' ? (isDark ? 'text-red-400' : 'text-red-500') : (isDark ? 'text-amber-400' : 'text-amber-500');
                const sentBg = t.sentiment === 'positive' ? (isDark ? 'bg-emerald-500/10' : 'bg-emerald-50') : t.sentiment === 'negative' ? (isDark ? 'bg-red-500/10' : 'bg-red-50') : (isDark ? 'bg-amber-500/10' : 'bg-amber-50');
                return (
                  <div key={i} className="flex items-center gap-2">
                    <span className={`text-[9px] flex-1 truncate ${tp}`}>{t.topic}</span>
                    <span className={`text-[8px] font-medium px-1.5 py-0.5 rounded-full ${sentBg} ${sentColor}`}>{t.sentiment}</span>
                    <span className={`text-[9px] font-medium w-6 text-right ${tp}`}>{t.count}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const InsightCard = ({ item, isDark }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const icons = { recommendation: Zap, alert: AlertTriangle, insight: TrendingUp };
  const colors = { recommendation: isDark ? 'text-indigo-400' : 'text-indigo-600', alert: isDark ? 'text-red-400' : 'text-red-500', insight: isDark ? 'text-emerald-400' : 'text-emerald-600' };
  const Icon = icons[item.type] || TrendingUp;
  return (
    <div className={`flex items-start gap-2 px-3 py-2 rounded-lg border ${border} ${isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]'}`} data-testid={`sentiment-insight-${item.id}`}>
      <Icon className={`w-3.5 h-3.5 flex-shrink-0 mt-0.5 ${colors[item.type]}`} />
      <div className="flex-1 min-w-0">
        <p className={`text-[11px] leading-relaxed ${tp}`}>{item.text}</p>
        <p className={`text-[9px] mt-1 ${ts}`}>Dora · {item.time}</p>
      </div>
    </div>
  );
};

const V4SupportSentiment = ({ isDark, onWorkflowToggle, workflowPanelOpen }) => {
  const [dateRange, setDateRange] = useState('weekly');
  const [customOpen, setCustomOpen] = useState(false);
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [message, setMessage] = useState('');
  const [chatMode, setChatMode] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const messagesEndRef = useRef(null);

  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const solidBg = isDark ? 'bg-[#0a1628]' : 'bg-[#eee8df]';
  const inputBorder = isDark ? 'border-white/[0.08]' : 'border-black/[0.08]';
  const hoverBg = isDark ? 'hover:bg-white/[0.06]' : 'hover:bg-black/[0.04]';

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [chatMessages]);

  // custom falls back to weekly data for the demo
  const dataKey = dateRange === 'custom' ? 'weekly' : dateRange;
  const currentData = SENTIMENT_DATA[dataKey];

  const dateRanges = [
    { id: 'today', label: 'Today' },
    { id: 'weekly', label: 'Weekly' },
    { id: 'monthly', label: 'Monthly' },
    { id: 'custom', label: 'Custom' },
  ];

  const sendMessage = (text, resp) => {
    setChatMessages(prev => [...prev, { role: 'user', text }, { role: 'agent', text: resp?.text || SENTIMENT_CHAT_RESPONSES.general.text }]);
    setChatMode(true);
  };
  const handleSend = () => { if (message.trim()) { sendMessage(message.trim()); setMessage(''); } };

  const chatInput = (
    <div className="flex justify-center pb-4 px-4 flex-shrink-0">
      <div className="w-[80%]">
        <div className={`${solidBg} border ${inputBorder} rounded-2xl flex flex-col`}>
          <div className="px-4 pt-3 pb-2">
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
              placeholder="Message Dora about sentiment data..." rows={1} className={`w-full bg-transparent resize-none outline-none text-sm leading-5 ${tp} placeholder:${ts}`} style={{ height: 'auto', maxHeight: '200px' }} data-testid="sentiment-chat-input" />
          </div>
          <div className="flex items-center justify-between px-3 pb-3">
            <div className="flex items-center gap-1">
              <div className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg ${isDark ? 'bg-white/[0.04]' : 'bg-black/[0.03]'}`}>
                <img src={AGENT_THUMBS.dora} alt="" className="w-5 h-5 rounded-full object-cover" /><span className={`text-xs font-medium ${tp}`}>Dora</span>
              </div>
              <button onClick={onWorkflowToggle} className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg transition-colors ${workflowPanelOpen ? (isDark ? 'bg-white/[0.08]' : 'bg-black/[0.06]') : hoverBg}`}>
                <Zap className={`w-3.5 h-3.5 ${tp}`} /><span className={`text-xs font-medium ${tp}`}>Workflows</span>
              </button>
            </div>
            <div className="flex items-center gap-1.5">
              <button className={`w-8 h-8 rounded-xl flex items-center justify-center ${hoverBg}`}><Mic className={`w-4 h-4 ${ts}`} /></button>
              <button onClick={handleSend} className={`w-8 h-8 rounded-xl flex items-center justify-center ${message.trim() ? 'bg-gradient-to-br from-indigo-500 to-purple-600' : (isDark ? 'bg-white/[0.06]' : 'bg-black/[0.04]')}`} data-testid="sentiment-send-btn">
                <Send className={`w-3.5 h-3.5 ${message.trim() ? 'text-white' : ts}`} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (chatMode) {
    return (
      <div className="flex flex-col h-full" data-testid="sentiment-chat-view">
        <div className={`px-4 py-3 flex-shrink-0 border-b ${border}`}>
          <button onClick={() => setChatMode(false)} className={`flex items-center gap-2 px-2 py-1 rounded-lg ${hoverBg}`} data-testid="sentiment-back-btn">
            <ArrowLeft className={`w-4 h-4 ${ts}`} /><span className={`text-sm font-medium ${tp}`}>Back to Sentiment</span>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto"><div className="flex justify-center"><div className="w-[80%] py-5 space-y-5">
          {chatMessages.map((msg, i) => (
            <div key={i} className={msg.role === 'user' ? 'flex justify-end' : 'flex gap-3'}>
              {msg.role === 'agent' && <img src={AGENT_THUMBS.dora} alt="" className="w-7 h-7 rounded-full object-cover flex-shrink-0 mt-1" />}
              <div className={msg.role === 'user' ? `max-w-[75%] px-3.5 py-2.5 rounded-2xl rounded-br-md text-sm leading-relaxed ${isDark ? 'bg-indigo-500/15 text-white' : 'bg-indigo-500/10 text-slate-800'}` : `text-sm leading-relaxed whitespace-pre-line ${tp}`}>{msg.text}</div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div></div></div>
        {chatInput}
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full" data-testid="sentiment-page">
      {/* Header */}
      <div className="px-5 pt-4 pb-3 flex-shrink-0">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <Heart className={`w-5 h-5 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} />
            <p className={`text-base font-semibold ${tp}`}>Sentiment</p>
            <span className={`text-[10px] ${ts}`}>{dateRange === 'custom' ? (customFrom && customTo ? `${customFrom} — ${customTo}` : 'Custom range') : currentData.label}</span>
          </div>
          <div className="flex items-center gap-1 relative">
            {dateRanges.map(r => (
              <button key={r.id} onClick={() => { setDateRange(r.id); if (r.id === 'custom') setCustomOpen(true); else setCustomOpen(false); }}
                className={`px-2.5 py-1 rounded-lg text-[10px] font-medium border transition-colors ${dateRange === r.id ? (isDark ? 'bg-indigo-500/15 text-indigo-400 border-indigo-500/20' : 'bg-indigo-500/10 text-indigo-600 border-indigo-500/20') : `${border} ${ts} ${hoverBg}`}`}
                data-testid={`sentiment-range-${r.id}`}
              >
                {r.id === 'custom' && <Calendar className="w-3 h-3 inline mr-1" />}
                {r.label}
              </button>
            ))}
            {/* Custom date picker dropdown */}
            {customOpen && dateRange === 'custom' && (
              <div className={`absolute top-full right-0 mt-2 rounded-xl p-3 z-10 ${isDark ? 'bg-[#0d1b2a] border border-white/[0.08]' : 'bg-white border border-black/[0.08]'} shadow-lg`} style={{ minWidth: '240px' }}>
                <p className={`text-[10px] font-semibold mb-2 ${tp}`}>Custom Range</p>
                <div className="flex items-center gap-2 mb-2">
                  <div className="flex-1">
                    <label className={`text-[9px] ${ts} block mb-0.5`}>From</label>
                    <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)}
                      className={`w-full text-[10px] px-2 py-1.5 rounded-lg border ${border} bg-transparent ${tp} outline-none`}
                      data-testid="sentiment-custom-from"
                    />
                  </div>
                  <div className="flex-1">
                    <label className={`text-[9px] ${ts} block mb-0.5`}>To</label>
                    <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)}
                      className={`w-full text-[10px] px-2 py-1.5 rounded-lg border ${border} bg-transparent ${tp} outline-none`}
                      data-testid="sentiment-custom-to"
                    />
                  </div>
                </div>
                <button onClick={() => setCustomOpen(false)}
                  className="w-full text-[10px] font-medium px-3 py-1.5 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 text-white"
                  data-testid="sentiment-custom-apply"
                >Apply</button>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className={`border-t ${border}`} />

      {/* Content */}
      <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 space-y-5">
        {/* Summary metrics */}
        <div className="grid grid-cols-6 gap-3">
          {currentData.metrics.map((m, i) => <MetricPill key={i} item={m} isDark={isDark} />)}
        </div>

        {/* Trend cards */}
        <div className="grid grid-cols-3 gap-3">
          {currentData.trends.map((t, i) => <TrendCard key={i} item={t} isDark={isDark} idx={i} />)}
        </div>

        {/* Brand Reputation */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Globe className={`w-4 h-4 ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`} />
            <p className={`text-xs font-semibold ${tp}`}>Online Brand Reputation</p>
          </div>
          <BrandReputationSection data={SENTIMENT_DATA.brandReputation} isDark={isDark} />
        </div>

        {/* Customer feedback */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <MessageSquare className={`w-4 h-4 ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`} />
            <p className={`text-xs font-semibold ${tp}`}>Recent Feedback</p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {SENTIMENT_DATA.feedback.map(fb => <FeedbackCard key={fb.id} fb={fb} isDark={isDark} />)}
          </div>
        </div>

        {/* Dora's insights */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <img src={AGENT_THUMBS.dora} alt="" className="w-4 h-4 rounded-full object-cover" />
            <p className={`text-xs font-semibold ${tp}`}>Dora's Insights</p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {SENTIMENT_DATA.insights.map(item => <InsightCard key={item.id} item={item} isDark={isDark} />)}
          </div>
        </div>
      </div>

      {chatInput}
    </div>
  );
};

export default V4SupportSentiment;
